import React, { useCallback, useEffect, useMemo, useState } from "react";
import {UserLimit, GameTag} from "../../http/protocol";
import {getContainer} from "../../ioc/IOCSetup";
import {t} from "../../i18n";
import "./UserGameLimits.scss";

interface Props {
    userId: number;
}

interface EditableLimit extends UserLimit {
    _editing?: boolean;
    _amountText?: string;
    _addingTag?: boolean;
    _tagQuery?: string;
    _saving?: boolean;
    _error?: string;
}

export const UserGameLimits: React.FC<Props> = ({ userId }) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [limits, setLimits] = useState<EditableLimit[]>([]);
    const [error, setError] = useState<string | undefined>();
    const [newLimitAmount, setNewLimitAmount] = useState<string | undefined>(undefined);
    const [newLimitReason, setNewLimitReason] = useState<string | undefined>(undefined);
    const [availableGameTags, setAvailableGameTags] = useState<GameTag[]>([]);
    const [newLimitTouched, setNewLimitTouched] = useState<boolean | undefined>(false);

    const nameByNameId = useMemo(() => {
        const m = new Map<string, string>();
        for (const tag of availableGameTags) {
            if (tag.nameId && tag.name) m.set(tag.nameId, tag.name);
        }
        return m;
    }, [availableGameTags]);

    const loadGameTags = useCallback(async () => {
        try {
            const service = getContainer().getCasinoGameTagService();
            const response = await service.list({ limit: 1000, offset: 0, orderBy: "name", ascending: true });
            setAvailableGameTags(response.items || []);
        } catch (e) {
            console.error("Failed to load game tags:", e);
        }
    }, []);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const service = getContainer().getUserLimitService();
            const list = await service.listByUser(userId);
            const sorted = (list || []).sort((a, b) => (b.euroLimit || 0) - (a.euroLimit || 0));
            const editable = sorted.map(l => ({ ...l, _amountText: (l.euroLimit ?? 0).toFixed(2) }));
            setLimits(editable);
            setLoading(false);
        } catch (e: any) {
            setError(e?.message || "Failed to load");
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        reload();
        loadGameTags();
    }, [reload, loadGameTags]);

    const updateLocalAmount = useCallback((idx: number, text: string) => {
        setLimits(prev => {
            const next = [...prev];
            next[idx] = { ...next[idx], _amountText: text, _editing: true, _error: undefined };
            return next;
        });
    }, []);

    const saveAmount = useCallback(async (idx: number) => {
        const row = limits[idx];
        const amount = Number.parseFloat(row._amountText || "0");
        if (Number.isNaN(amount) || amount < 0) {
            setLimits(prev => {
                const next = [...prev];
                next[idx] = { ...next[idx], _error: t("validation.invalidAmount") || "Invalid amount" };
                return next;
            });
            return;
        }
        try {
            setSaving(true);
            setLimits(prev => {
                const next = [...prev];
                next[idx] = { ...next[idx], _saving: true };
                return next;
            });
            const service = getContainer().getUserLimitService();
            const updated = await service.update(userId, row.id!, { euroLimit: amount });
            setLimits(prev => {
                const next = [...prev];
                next[idx] = { ...next[idx], euroLimit: updated.euroLimit, _editing: false, _amountText: (updated.euroLimit ?? 0).toFixed(2), _saving: false };
                next.sort((a, b) => (b.euroLimit || 0) - (a.euroLimit || 0));
                return next;
            });
            setSaving(false);
        } catch (e: any) {
            setError(e?.message || "Failed to update");
            setLimits(prev => {
                const next = [...prev];
                if (next[idx]) next[idx] = { ...next[idx], _saving: false };
                return next;
            });
        } finally {
            setSaving(false);
        }
    }, [limits, userId]);

    const deleteLimit = useCallback(async (idx: number) => {
        const row = limits[idx];
        if (!row?.id) return;
        if (!window.confirm(t("confirm.delete") || "Are you sure?")) return;
        try {
            const service = getContainer().getUserLimitService();
            await service.delete(userId, row.id);
            setLimits(prev => prev.filter((_, i) => i !== idx));
        } catch (e: any) {
            setError(e?.message || "Failed to delete");
        }
    }, [limits, userId]);

    const addTag = useCallback(async (idx: number) => {
        const row = limits[idx];
        if (!row._tagQuery) return;
        if ((row.tags || []).includes(row._tagQuery)) return;
        try {
            setLimits(prev => {
                const next = [...prev];
                next[idx] = { ...next[idx], _saving: true };
                return next;
            });
            const service = getContainer().getUserLimitService();
            await service.addTag(userId, row.id!, row._tagQuery);
            setLimits(prev => {
                const next = [...prev];
                const updatedRow = next[idx];
                const updatedTags = [ ...(updatedRow.tags || []), updatedRow._tagQuery as string ];
                next[idx] = { ...updatedRow, tags: updatedTags, _tagQuery: "", _addingTag: false, _saving: false };
                return next;
            });
        } catch (e: any) {
            setError(e?.message || "Failed to add tag");
            setLimits(prev => {
                const next = [...prev];
                if (next[idx]) next[idx] = { ...next[idx], _saving: false };
                return next;
            });
        }
    }, [limits, userId]);

    const removeTag = useCallback(async (idx: number, tag: string) => {
        const row = limits[idx];
        try {
            setLimits(prev => {
                const next = [...prev];
                next[idx] = { ...next[idx], _saving: true };
                return next;
            });
            const service = getContainer().getUserLimitService();
            await service.removeTag(userId, row.id!, tag);
            setLimits(prev => {
                const next = [...prev];
                next[idx] = { ...row, tags: (row.tags || []).filter(tg => tg !== tag), _saving: false };
                return next;
            });
        } catch (e: any) {
            setError(e?.message || "Failed to remove tag");
            setLimits(prev => {
                const next = [...prev];
                if (next[idx]) next[idx] = { ...next[idx], _saving: false };
                return next;
            });
        }
    }, [limits, userId]);

    const invalidNewAmount = useMemo(() => {
        const v = parseFloat(newLimitAmount || "");
        return Number.isNaN(v) || v < 0;
    }, [newLimitAmount]);

    const addLimit = useCallback(async () => {
        try {
            const amount = parseFloat(newLimitAmount || "0");
            const reason = newLimitReason || "";
            if (isNaN(amount) || amount < 0) {
                setError("Invalid amount");
                return;
            }
            setSaving(true);
            const service = getContainer().getUserLimitService();
            const created = await service.create(userId, { euroLimit: amount, reason });
            setLimits(prev => {
                const next = [
                    ...prev,
                    { ...created, _amountText: (created.euroLimit ?? 0).toFixed(2) }
                ].sort((a, b) => (b.euroLimit || 0) - (a.euroLimit || 0));
                return next;
            });
            setNewLimitAmount("");
            setNewLimitReason("");
            setNewLimitTouched(false);
            setError(undefined);
        } catch (e: any) {
            setError(e?.message || "Failed to create");
        } finally {
            setSaving(false);
        }
    }, [newLimitAmount, newLimitReason, userId]);

    const renderRow = useCallback((row: EditableLimit, idx: number) => {
        const invalidRowAmount = Number.isNaN(Number.parseFloat(row._amountText || "")) || Number.parseFloat(row._amountText || "-1") < 0;
        const rowBusy = !!row._saving;
        return (
            <div className="ugl-limit" key={row.id || idx}>
                <div className="ugl-limit-header">
                    <div className="ugl-amount-section">
                        <input 
                            type="number"
                            inputMode="decimal"
                            step="1"
                            min="0"
                            className="form-control ugl-amount-pill" 
                            value={row._amountText || ""} 
                            onChange={e => {
                                updateLocalAmount(idx, e.target.value);
                                if (error) setError(undefined);
                            }}
                            onBlur={() => {
                                setLimits(prev => {
                                    const next = [...prev];
                                    const current = next[idx];
                                    const v = parseFloat(current._amountText || "");
                                    if (!Number.isNaN(v) && v >= 0) {
                                        next[idx] = { ...current, _amountText: v.toFixed(2) };
                                    }
                                    return next;
                                });
                            }}
                        />
                        {(row._error || (row._editing && invalidRowAmount)) && (
                            <small className="text-danger ugl-inline-error">{row._error || (t("validation.invalidAmount") || "Invalid amount")}</small>
                        )}
                        {row.reason && (
                            <div className="ugl-reason-text">
                                {row.reason}
                            </div>
                        )}
                    </div>
                    <div className="ugl-header-actions">
                        <button className="btn btn-success" onClick={() => saveAmount(idx)} disabled={saving || rowBusy || invalidRowAmount}>
                            {t("button.save") || "Save"}
                        </button>
                        <button 
                            className="btn btn-outline-danger" 
                            onClick={() => deleteLimit(idx)} 
                            disabled={saving || rowBusy}
                            aria-label={t("button.deleteLimit") || "Delete limit"}
                            title={t("button.deleteLimit") || "Delete limit"}
                        >
                            {t("button.delete") || "Delete"}
                        </button>
                    </div>
                </div>
                <div className="ugl-limit-body">
                    {(row.tags || []).map(tagValue => {
                        const displayName = nameByNameId.get(tagValue) || tagValue;
                        return (
                            <div className="ugl-tag-row" key={tagValue}>
                                <span className="form-control ugl-tag-input" aria-label={t("label.gameTag") || "Game tag"}>{displayName}</span>
                                <button 
                                    className="btn btn-outline-secondary ugl-tag-remove" 
                                    onClick={() => removeTag(idx, tagValue)} 
                                    disabled={rowBusy}
                                    aria-label={t("button.removeTag") || "Remove tag"}
                                    title={t("button.removeTag") || "Remove tag"}
                                >
                                    {t("button.remove") || "Remove"}
                                </button>
                            </div>
                        );
                    })}
                    <div className="ugl-add-row">
                        <select 
                            className="form-control" 
                            value={row._tagQuery || ""} 
                            onChange={e => {
                                setLimits(prev => {
                                    const next = [...prev];
                                    next[idx] = { ...next[idx], _tagQuery: e.target.value };
                                    return next;
                                });
                            }}
                            disabled={rowBusy}
                        >
                            <option value="">{t("label.selectGameTag") || "Select a game tag"}</option>
                            {availableGameTags
                                .filter(tag => !(row.tags || []).includes(tag.name || ""))
                                .map(tag => (
                                    <option key={tag.id} value={tag.nameId}>
                                        {tag.name}
                                    </option>
                                ))}
                        </select>
                        <button 
                            className="btn btn-primary" 
                            onClick={() => addTag(idx)}
                            disabled={rowBusy || !row._tagQuery || (row.tags || []).includes(row._tagQuery)}
                        >
                            {t("button.add") || "Add"}
                        </button>
                    </div>
                </div>
            </div>
        );
    }, [availableGameTags, deleteLimit, error, removeTag, saveAmount, saving, updateLocalAmount]);

    return (
        <div className="ugl-card card-box">
            <div className="ugl-header">
                <h4 className="ugl-title">{t("label.userMaxBetLimits") || "User max bet limits"}</h4>
            </div>
            {error && <div className="alert alert-danger ugl-error">{error}</div>}
            {loading ? (
                <div className="ugl-loading">{t("label.loading") || "Loading..."}</div>
            ) : (
                <>
                    <div className="ugl-body">
                        {limits.length === 0 && <div className="ugl-empty">{t("label.noLimits") || "No limits set"}</div>}
                        {limits.map((row, idx) => renderRow(row, idx))}
                    </div>
                    <div className="ugl-footer">
                        <div className="ugl-new-limit-form">
                            <div className="ugl-form-row">
                                <input 
                                    type="number"
                                    inputMode="decimal"
                                    step="1"
                                    min="0"
                                    className="form-control ugl-amount-input" 
                                    placeholder={t("placeholder.amount") || "Amount (€)"}
                                    value={newLimitAmount || ""} 
                                    onChange={e => { setNewLimitAmount(e.target.value); setError(undefined); setNewLimitTouched(true); }}
                                    onBlur={() => {
                                        const v = parseFloat(newLimitAmount || "");
                                        if (!Number.isNaN(v) && v >= 0) setNewLimitAmount(v.toFixed(2));
                                    }}
                                />
                                {newLimitTouched && invalidNewAmount && (
                                    <small className="text-danger ugl-inline-error">{t("validation.invalidAmount") || "Invalid amount"}</small>
                                )}
                                <input 
                                    className="form-control ugl-reason-input" 
                                    placeholder={t("placeholder.reason") || "Reason"}
                                    value={newLimitReason || ""} 
                                    onChange={e => setNewLimitReason(e.target.value)}
                                />
                                <button className="btn btn-primary" onClick={addLimit} disabled={saving || invalidNewAmount}>
                                    {t("button.addEuroLimit") || "Add € limit"}
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default UserGameLimits;


